import React, { useState, useMemo } from 'react';
import type { ContainerStatus, ContainerAction, SortField, SortDirection } from '../types';

interface ContainerStatusTableProps {
  containers: ContainerStatus[];
  onAction: (tenantId: string, action: ContainerAction) => void;
  onViewLogs: (container: ContainerStatus) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

const STATUS_STYLES: Record<ContainerStatus['status'], { label: string; className: string }> = {
  running: { label: 'Running', className: 'status-badge status-active' },
  stopped: { label: 'Stopped', className: 'status-badge status-suspended' },
  unhealthy: { label: 'Unhealthy', className: 'status-badge status-suspended' },
  not_provisioned: { label: 'Not Provisioned', className: 'status-badge status-provisioning' },
  unknown: { label: 'Unknown', className: 'status-badge status-suspended' },
};

const SORTABLE_HEADERS: Array<{ field: SortField; label: string }> = [
  { field: 'tenant_slug', label: 'Tenant' },
  { field: 'tenant_name', label: 'Name' },
  { field: 'status', label: 'Status' },
  { field: 'version', label: 'Version' },
  { field: 'cpu_percent', label: 'CPU %' },
  { field: 'memory_mb', label: 'Memory (MB)' },
];

export const ContainerStatusTable: React.FC<ContainerStatusTableProps> = ({
  containers,
  onAction,
  onViewLogs,
  selectedIds,
  onSelectionChange,
}) => {
  const [sortField, setSortField] = useState<SortField>('tenant_slug');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const copy = [...containers];
    copy.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [containers, sortField, sortDir]);

  const allSelected = containers.length > 0 && selectedIds.size === containers.length;

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(containers.map((c) => c.tenant_id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  return (
    <div className="glass-card" style={{ overflow: 'auto' }}>
      <table
        style={{ width: '100%', borderCollapse: 'collapse' }}
        role="grid"
        aria-label="Container status table"
      >
        <thead>
          <tr>
            <th style={{ padding: '12px 8px', textAlign: 'left' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                aria-label="Select all containers"
              />
            </th>
            {SORTABLE_HEADERS.map((h) => (
              <th
                key={h.field}
                style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '13px',
                  opacity: 0.7,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => handleSort(h.field)}
                aria-sort={sortField === h.field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                {h.label} {sortField === h.field ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
              </th>
            ))}
            <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', opacity: 0.7 }}>
              Uptime
            </th>
            <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', opacity: 0.7 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const st = STATUS_STYLES[c.status];
            return (
              <tr
                key={c.tenant_id}
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <td style={{ padding: '10px 8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.tenant_id)}
                    onChange={() => toggleOne(c.tenant_id)}
                    aria-label={`Select ${c.tenant_slug}`}
                  />
                </td>
                <td style={{ padding: '10px 8px', fontWeight: 600 }}>{c.tenant_slug}</td>
                <td style={{ padding: '10px 8px' }}>{c.tenant_name}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span className={st.className}>{st.label}</span>
                </td>
                <td style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                  {c.version}
                </td>
                <td style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                  {c.cpu_percent.toFixed(1)}
                </td>
                <td style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                  {c.memory_mb}
                </td>
                <td style={{ padding: '10px 8px', fontSize: '13px', opacity: 0.7 }}>
                  {c.uptime}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                    {c.status === 'stopped' ? (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => onAction(c.tenant_id, 'start')}
                        aria-label={`Start ${c.tenant_slug}`}
                      >
                        Start
                      </button>
                    ) : c.status === 'running' || c.status === 'unhealthy' ? (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => onAction(c.tenant_id, 'stop')}
                          aria-label={`Stop ${c.tenant_slug}`}
                        >
                          Stop
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => onAction(c.tenant_id, 'restart')}
                          aria-label={`Restart ${c.tenant_slug}`}
                        >
                          Restart
                        </button>
                      </>
                    ) : null}
                    {c.status !== 'not_provisioned' && (
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => onViewLogs(c)}
                        aria-label={`View logs for ${c.tenant_slug}`}
                      >
                        Logs
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} style={{ padding: '24px', textAlign: 'center', opacity: 0.5 }}>
                No containers found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
